import type { Schemas } from "@platform/contracts/client";
import { Button, Card, Input, Label, Select } from "@platform/ui";
import {
  AttributeFields,
  draftOf,
  ErrorText,
  errorKey,
  issuesOf,
  issueText,
  type LocalizedDraft,
  LocalizedFields,
  localName,
  textOf,
  useSession,
  useText,
} from "@platform/ui/features";
import { type FormEvent, useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router";
import { useTranslations } from "use-intl";
import type { OpsCategory } from "../lib/catalog";
import { locale } from "../lib/i18n";

type Definition = Schemas["AttributeDefinition"];
type Type = Definition["type"];
type Usage = Schemas["AttributeUsage"];

interface OptionDraft {
  readonly row: string;
  readonly value: string;
  readonly label: LocalizedDraft;
  /** In the saved schema, so products may hold it (card 048). */
  readonly saved: boolean;
}

/** An attribute as the editor holds it: every type's fields, so switching type keeps what was typed. */
export interface AttributeDraft {
  readonly row: string;
  /** In the saved schema, so products may hold values for it (card 048). */
  readonly saved: boolean;
  readonly key: string;
  readonly label: LocalizedDraft;
  readonly type: Type;
  readonly required: boolean;
  readonly filterable: boolean;
  readonly maxLength: string;
  readonly unit: string;
  readonly min: string;
  readonly max: string;
  readonly integer: boolean;
  readonly multiple: boolean;
  readonly options: readonly OptionDraft[];
}

const TYPES: readonly Type[] = ["text", "number", "choice", "boolean"];
const NUMBER = /^-?\d+(\.\d+)?$/;
let rows = 0;
const row = () => {
  rows += 1;
  return `a${rows}`;
};

const newOption = (): OptionDraft => ({ row: row(), value: "", label: draftOf(null), saved: false });

export const newAttribute = (): AttributeDraft => ({
  row: row(),
  saved: false,
  key: "",
  label: draftOf(null),
  type: "text",
  required: false,
  filterable: false,
  maxLength: "200",
  unit: "",
  min: "",
  max: "",
  integer: false,
  multiple: false,
  options: [newOption(), newOption()],
});

export function draftOfDefinition(definition: Definition): AttributeDraft {
  const base = {
    ...newAttribute(),
    saved: true,
    key: definition.key,
    label: draftOf(definition.label),
    type: definition.type,
  };
  const common = { ...base, required: definition.required, filterable: definition.filterable };
  switch (definition.type) {
    case "text":
      return { ...common, maxLength: String(definition.maxLength) };
    case "number":
      return {
        ...common,
        unit: definition.unit ?? "",
        min: definition.min === null ? "" : String(definition.min),
        max: definition.max === null ? "" : String(definition.max),
        integer: definition.integer,
      };
    case "choice":
      return {
        ...common,
        multiple: definition.multiple,
        options: definition.options.map((option) => ({
          row: row(),
          value: option.value,
          label: draftOf(option.label),
          saved: true,
        })),
      };
    default:
      return common;
  }
}

/** The drafts as the API takes them, with what cannot be read reported at its field (`attributes.0.min`). */
export function definitionsOf(drafts: readonly AttributeDraft[]): {
  attributes: Definition[];
  issues: Record<string, string>;
} {
  const issues: Record<string, string> = {};
  const number = (path: string, text: string) => {
    const value = text.trim();
    if (value === "") return null;
    if (!NUMBER.test(value)) {
      issues[path] = "INVALID_TYPE";
      return null;
    }
    // An attribute's limit is a measurement, such as a diameter; money never passes through here.
    return Number(value);
  };
  const attributes = drafts.map((draft, a): Definition => {
    const at = `attributes.${a}`;
    if (draft.label.en.trim() === "") issues[`${at}.label.en`] = "REQUIRED";
    const common = {
      key: draft.key.trim(),
      label: textOf(draft.label),
      required: draft.required,
      filterable: draft.filterable,
    };
    switch (draft.type) {
      case "text":
        return { ...common, type: "text", maxLength: number(`${at}.maxLength`, draft.maxLength) ?? 0 };
      case "number":
        return {
          ...common,
          type: "number",
          unit: draft.unit.trim() === "" ? null : draft.unit.trim(),
          min: number(`${at}.min`, draft.min),
          max: number(`${at}.max`, draft.max),
          integer: draft.integer,
        };
      case "choice":
        draft.options.forEach((option, o) => {
          if (option.label.en.trim() === "") issues[`${at}.options.${o}.label.en`] = "REQUIRED";
        });
        return {
          ...common,
          type: "choice",
          multiple: draft.multiple,
          options: draft.options.map((option) => ({
            value: option.value.trim(),
            label: textOf(option.label),
          })),
        };
      default:
        return { ...common, type: "boolean" };
    }
  });
  return { attributes, issues };
}

const KEY = /^[a-z][a-z0-9_]{1,39}$/;

/** What the preview can show while the schema is being typed: attributes and options with keys that are valid and unique. */
export function previewable(attributes: readonly Definition[]): Definition[] {
  return attributes
    .filter(
      (attribute, a) =>
        KEY.test(attribute.key) && attributes.findIndex((each) => each.key === attribute.key) === a,
    )
    .map((attribute) =>
      attribute.type === "choice"
        ? {
            ...attribute,
            options: attribute.options.filter(
              (option, o) =>
                KEY.test(option.value) &&
                attribute.options.findIndex((each) => each.value === option.value) === o,
            ),
          }
        : attribute,
    );
}

function AttributeEditor({
  draft,
  index,
  count,
  use,
  issue,
  onChange,
  onMove,
  onRemove,
  onConfirm,
}: {
  draft: AttributeDraft;
  index: number;
  count: number;
  /** What products hold for this key, when it is in the saved schema. */
  use: Usage | undefined;
  issue: (path: string) => string | undefined;
  onChange: (draft: AttributeDraft) => void;
  onMove: (by: -1 | 1) => void;
  onRemove: () => void;
  /** Records a removal the operator confirmed: a key, or `key.value` for a choice value. */
  onConfirm: (removal: string) => void;
}) {
  const t = useTranslations();
  const at = `attributes.${index}`;
  const id = `attribute-${index}`;
  const set = (changes: Partial<AttributeDraft>) => onChange({ ...draft, ...changes });
  // A key products hold keeps its key and type (card 048); removing it, or a value they chose, is confirmed first.
  const held = draft.saved ? (use?.products ?? 0) : 0;
  const chosen = (option: OptionDraft) =>
    draft.saved && option.saved
      ? (use?.options.find((each) => each.value === option.value)?.products ?? 0)
      : 0;
  const [confirming, setConfirming] = useState<string | undefined>();
  const error = (path: string) => {
    const text = issue(path);
    return text === undefined ? null : <p className="text-caption text-tone-problem-fg">{text}</p>;
  };
  return (
    <fieldset className="flex flex-col gap-3 rounded-(--radius-control) border border-border p-3">
      <legend className="px-1 text-small font-medium">
        {t("admin.schema.attribute", { number: index + 1 })}
      </legend>
      {held > 0 && (
        <p className="text-caption text-ink-muted">
          {t("admin.schema.usage", {
            count: held,
            detail: (use?.byStatus ?? [])
              .map((each) => `${t(`admin.products.status.${each.status}`)} ${each.products}`)
              .join(", "),
          })}{" "}
          {t("admin.schema.locked")}
        </p>
      )}
      <div className="flex flex-wrap gap-2">
        <Button type="button" size="sm" variant="ghost" disabled={index === 0} onClick={() => onMove(-1)}>
          {t("admin.schema.up")}
        </Button>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          disabled={index === count - 1}
          onClick={() => onMove(1)}
        >
          {t("admin.schema.down")}
        </Button>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          onClick={() => (held > 0 ? setConfirming("attribute") : onRemove())}
        >
          {t("admin.schema.remove")}
        </Button>
      </div>
      {confirming === "attribute" && (
        <div className="flex flex-wrap items-center gap-2 rounded border border-border bg-muted p-2 text-small">
          <span>{t("admin.schema.confirmAttribute", { count: held })}</span>
          <Button
            type="button"
            size="sm"
            variant="secondary"
            onClick={() => {
              onConfirm(draft.key);
              onRemove();
            }}
          >
            {t("admin.schema.confirm")}
          </Button>
          <Button type="button" size="sm" variant="ghost" onClick={() => setConfirming(undefined)}>
            {t("admin.schema.keep")}
          </Button>
        </div>
      )}
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={`${id}-key`}>{t("admin.schema.key")}</Label>
          <Input
            id={`${id}-key`}
            value={draft.key}
            maxLength={40}
            readOnly={held > 0}
            onChange={(event) => set({ key: event.target.value })}
          />
          <p className="text-caption text-ink-muted">{t("admin.schema.keyHint")}</p>
          {error(`${at}.key`)}
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={`${id}-type`}>{t("admin.schema.type")}</Label>
          <Select
            id={`${id}-type`}
            value={draft.type}
            disabled={held > 0}
            onChange={(event) => set({ type: event.target.value as Type })}
          >
            {TYPES.map((type) => (
              <option key={type} value={type}>
                {t(`admin.schema.types.${type}`)}
              </option>
            ))}
          </Select>
          {error(`${at}.type`)}
        </div>
      </div>
      <LocalizedFields
        id={`${id}-label`}
        label={t("admin.schema.label")}
        value={draft.label}
        maxLength={120}
        onChange={(label) => set({ label })}
        errors={{ en: issue(`${at}.label.en`), zh: issue(`${at}.label.zh`) }}
      />
      <div className="flex flex-wrap gap-4 text-small">
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={draft.required}
            onChange={(event) => set({ required: event.target.checked })}
          />
          {t("admin.schema.required")}
        </label>
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={draft.filterable}
            onChange={(event) => set({ filterable: event.target.checked })}
          />
          {t("admin.schema.filterable")}
        </label>
      </div>
      {draft.type === "text" && (
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={`${id}-max-length`}>{t("admin.schema.maxLength")}</Label>
          <Input
            id={`${id}-max-length`}
            className="w-32"
            inputMode="numeric"
            value={draft.maxLength}
            onChange={(event) => set({ maxLength: event.target.value })}
          />
          {error(`${at}.maxLength`)}
        </div>
      )}
      {draft.type === "number" && (
        <div className="grid gap-3 sm:grid-cols-3">
          {(["unit", "min", "max"] as const).map((field) => (
            <div key={field} className="flex flex-col gap-1.5">
              <Label htmlFor={`${id}-${field}`}>{t(`admin.schema.${field}`)}</Label>
              <Input
                id={`${id}-${field}`}
                value={draft[field]}
                maxLength={20}
                onChange={(event) => set({ [field]: event.target.value })}
              />
              {error(`${at}.${field}`)}
            </div>
          ))}
          <label className="flex items-center gap-2 text-small">
            <input
              type="checkbox"
              checked={draft.integer}
              onChange={(event) => set({ integer: event.target.checked })}
            />
            {t("admin.schema.integer")}
          </label>
        </div>
      )}
      {draft.type === "choice" && (
        <div className="flex flex-col gap-3">
          <label className="flex items-center gap-2 text-small">
            <input
              type="checkbox"
              checked={draft.multiple}
              disabled={held > 0}
              onChange={(event) => set({ multiple: event.target.checked })}
            />
            {t("admin.schema.multiple")}
          </label>
          {error(`${at}.multiple`)}
          {draft.options.map((option, o) => (
            <div key={option.row} className="flex flex-col gap-2 rounded border border-border p-2">
              <div className="flex items-end gap-2">
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor={`${id}-option-${o}`}>{t("admin.schema.optionValue")}</Label>
                  <Input
                    id={`${id}-option-${o}`}
                    value={option.value}
                    maxLength={40}
                    readOnly={chosen(option) > 0}
                    onChange={(event) =>
                      set({
                        options: draft.options.map((each, i) =>
                          i === o ? { ...each, value: event.target.value } : each,
                        ),
                      })
                    }
                  />
                </div>
                {chosen(option) > 0 && (
                  <span className="pb-2 text-caption text-ink-muted">
                    {t("admin.schema.optionUsage", { count: chosen(option) })}
                  </span>
                )}
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  onClick={() =>
                    chosen(option) > 0
                      ? setConfirming(option.row)
                      : set({ options: draft.options.filter((_, i) => i !== o) })
                  }
                >
                  {t("admin.schema.removeOption")}
                </Button>
              </div>
              {confirming === option.row && (
                <div className="flex flex-wrap items-center gap-2 rounded bg-muted p-2 text-small">
                  <span>{t("admin.schema.confirmOption", { count: chosen(option) })}</span>
                  <Button
                    type="button"
                    size="sm"
                    variant="secondary"
                    onClick={() => {
                      onConfirm(`${draft.key}.${option.value}`);
                      setConfirming(undefined);
                      set({ options: draft.options.filter((_, i) => i !== o) });
                    }}
                  >
                    {t("admin.schema.confirm")}
                  </Button>
                  <Button type="button" size="sm" variant="ghost" onClick={() => setConfirming(undefined)}>
                    {t("admin.schema.keep")}
                  </Button>
                </div>
              )}
              {error(`${at}.options.${o}.value`)}
              <LocalizedFields
                id={`${id}-option-${o}-label`}
                label={t("admin.schema.optionLabel")}
                value={option.label}
                maxLength={120}
                onChange={(label) =>
                  set({ options: draft.options.map((each, i) => (i === o ? { ...each, label } : each)) })
                }
                errors={{ en: issue(`${at}.options.${o}.label.en`) }}
              />
            </div>
          ))}
          {error(`${at}.options`)}
          <Button
            type="button"
            size="sm"
            variant="secondary"
            className="self-start"
            onClick={() => set({ options: [...draft.options, newOption()] })}
          >
            {t("admin.schema.addOption")}
          </Button>
        </div>
      )}
    </fieldset>
  );
}

/**
 * A leaf's attribute schema (CAT-2, ADM-4, card 044), edited field by field beside a preview of the seller's form.
 * Saving makes a new version; live products meet it at their next edit, and the count of those still on an older
 * version is shown.
 */
export function CategorySchemaPage() {
  const t = useTranslations();
  const text = useText();
  const { categoryId = "" } = useParams();
  const { session } = useSession();
  const [category, setCategory] = useState<OpsCategory | undefined>();
  const [drafts, setDrafts] = useState<readonly AttributeDraft[]>([]);
  const [usage, setUsage] = useState<ReadonlyMap<string, Usage>>(new Map());
  const [confirmed, setConfirmed] = useState<readonly string[]>([]);
  const [values, setValues] = useState<Record<string, string | readonly string[]>>({});
  const [issues, setIssues] = useState<Record<string, string>>({});
  const [notice, setNotice] = useState<string | undefined>();
  const [error, setError] = useState<string | undefined>();

  const show = useCallback(
    async (next: OpsCategory) => {
      const used = await session.api.GET("/v1/ops/categories/{categoryId}/attribute-usage", {
        params: { path: { categoryId } },
      });
      setUsage(new Map((used.data?.items ?? []).map((each) => [each.key, each])));
      setConfirmed([]);
      setCategory(next);
      setDrafts((next.attributeSchema?.attributes ?? []).map(draftOfDefinition));
    },
    [session, categoryId],
  );

  const load = useCallback(async () => {
    const { data, error: problem } = await session.api.GET("/v1/ops/categories");
    if (data === undefined) return setError(t(errorKey(problem)));
    const found = data.items.find((each) => each.id === categoryId);
    if (found === undefined) return setError(t("errors.NOT_FOUND"));
    await show(found);
  }, [session, categoryId, show, t]);

  useEffect(() => {
    void load();
  }, [load]);

  if (category === undefined) return error === undefined ? null : <ErrorText>{error}</ErrorText>;
  const { attributes, issues: local } = definitionsOf(drafts);
  const issue = (path: string) => issueText(text.t, issues[path]);
  const known = (path: string) =>
    /^attributes\.\d+\.(key|type|multiple|label\.(en|zh)|maxLength|unit|min|max|options(\.\d+\.(value|label\.(en|zh)))?)$/.test(
      path,
    );
  const elsewhere = Object.entries(issues).filter(([path]) => !known(path));

  const save = async (event: FormEvent) => {
    event.preventDefault();
    setNotice(undefined);
    setIssues(local);
    if (Object.keys(local).length > 0) return;
    const { data, error: problem } = await session.api.PUT(
      "/v1/ops/categories/{categoryId}/attribute-schema",
      {
        params: { path: { categoryId }, header: { "if-match": String(category.version) } },
        body: { attributes, confirmRemoval: [...confirmed] },
      },
    );
    if (data !== undefined) {
      await show(data);
      return setNotice(t("admin.schema.saved", { version: data.attributeSchemaVersion }));
    }
    if ((problem as { code?: string } | undefined)?.code === "VERSION_CONFLICT") {
      await load();
      return setIssues({ attributes: "CONFLICT" });
    }
    setIssues(issuesOf(problem) ?? { attributes: errorKey(problem) });
  };

  return (
    <div className="flex flex-col gap-4">
      <Link to="/categories" className="text-small text-brand-600 hover:text-brand-700">
        {t("admin.schema.back")}
      </Link>
      <h1 className="text-title font-semibold text-ink">
        {t("admin.schema.title", { category: localName(category.name, locale) })}
      </h1>
      {!category.isLeaf ? (
        <p className="text-small text-ink-muted">{t("admin.schema.notLeaf")}</p>
      ) : (
        <>
          <p className="text-small text-ink-muted">
            {t("admin.schema.version", {
              version: category.attributeSchemaVersion,
              products: category.productCount,
              older: category.liveOnOlderSchema,
            })}
          </p>
          <div className="grid gap-4 lg:grid-cols-[1fr_22rem]">
            <form className="flex flex-col gap-4" onSubmit={(event) => void save(event)}>
              {drafts.map((draft, index) => (
                <AttributeEditor
                  key={draft.row}
                  draft={draft}
                  index={index}
                  count={drafts.length}
                  use={usage.get(draft.key)}
                  issue={issue}
                  onChange={(next) =>
                    setDrafts((all) => all.map((each) => (each.row === next.row ? next : each)))
                  }
                  onMove={(by) =>
                    setDrafts((all) => {
                      const next = [...all];
                      const target = index + by;
                      [next[index], next[target]] = [
                        next[target] as AttributeDraft,
                        next[index] as AttributeDraft,
                      ];
                      return next;
                    })
                  }
                  onRemove={() => setDrafts((all) => all.filter((each) => each.row !== draft.row))}
                  onConfirm={(removal) => setConfirmed((all) => [...all, removal])}
                />
              ))}
              <Button
                type="button"
                variant="secondary"
                className="self-start"
                disabled={drafts.length >= 40}
                onClick={() => setDrafts((all) => [...all, newAttribute()])}
              >
                {t("admin.schema.add")}
              </Button>
              {elsewhere.map(([path, code]) => (
                <ErrorText key={path}>
                  {code === "CONFLICT"
                    ? t("admin.categories.conflict")
                    : code.startsWith("errors.")
                      ? t(code)
                      : `${path}: ${issueText(text.t, code) ?? code}`}
                </ErrorText>
              ))}
              {notice !== undefined && <p className="text-small text-tone-done-fg">{notice}</p>}
              <Button type="submit" className="self-start">
                {t("admin.schema.save")}
              </Button>
            </form>
            <Card className="flex h-fit flex-col gap-3">
              <h2 className="text-subheading font-semibold text-ink">{t("admin.schema.preview")}</h2>
              <AttributeFields
                schema={{ version: category.attributeSchemaVersion + 1, attributes: previewable(attributes) }}
                values={values}
                onChange={(key, value) => setValues((current) => ({ ...current, [key]: value }))}
                issue={() => undefined}
              />
            </Card>
          </div>
        </>
      )}
    </div>
  );
}
